"use client"

import { useNavigate } from "react-router-dom"
import { AlertTriangle, BarChart3, FileSpreadsheet, Flame, Package, PackageX, RotateCcw, Search, ShoppingBag } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import MultiSelectFilterCombobox from "./MultiSelectFilterCombobox"
import useInventoryAnalytics from "./hooks/useInventoryAnalytics"
import InventoryAnalyticsTable from "./InventoryAnalyticsTable"
import InventoryAnalyticsAnalysisTab from "./InventoryAnalyticsAnalysisTab"
import InventoryAnalyticsCharts from "./InventoryAnalyticsCharts"

const TABS = [
  { id: "all", label: "All Items" },
  { id: "high-running", label: "High Running", icon: Flame, summaryKey: "highRunning" },
  { id: "stock-out-risk", label: "Stock-Out Risk", icon: AlertTriangle, summaryKey: "stockOutRisk" },
  { id: "non-moving", label: "Non-Moving", icon: PackageX, summaryKey: "nonMoving" },
  { id: "analysis", label: "Analysis", icon: BarChart3 },
]

const SUMMARY_CARDS = [
  { key: "totalItems", label: "Total Items", icon: Package, accent: "text-[#0263cc]", bg: "bg-[#0263cc]/10" },
  { key: "highRunning", label: "High Running", icon: Flame, accent: "text-green-600", bg: "bg-green-50" },
  { key: "stockOutRisk", label: "Stock-Out Risk", icon: AlertTriangle, accent: "text-red-600", bg: "bg-red-50" },
  { key: "reorderRequired", label: "Reorder Required", icon: RotateCcw, accent: "text-orange-600", bg: "bg-orange-50" },
  { key: "nonMoving", label: "Non-Moving", icon: PackageX, accent: "text-slate-600", bg: "bg-slate-100" },
]

const DAYS_OPTIONS = [
  { value: "7d", label: "7D" },
  { value: "30d", label: "30D" },
  { value: "60d", label: "60D" },
  { value: "90d", label: "90D" },
]

const InventoryAnalyticsDashboard = () => {
  const navigate = useNavigate()
  const {
    categories,
    subCategoryOptions,
    brandOptions,
    sizes,
    colors,
    isColorEnabled,
    isSubCategoryEnabled,
    searchTerm,
    setSearchTerm,
    filterOptions,
    setFilterOptions,
    daysFilter,
    setDaysFilter,
    activeTab,
    setActiveTab,
    items,
    summary,
    loading,
    error,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalCount,
    analysis,
    analysisLoading,
    analysisError,
    charts,
    allItemsLoading,
    unitsSold,
  } = useInventoryAnalytics()

  const daysLabel = DAYS_OPTIONS.find((d) => d.value === daysFilter)?.label || "30D"

  return (
    <div className="w-full mx-auto space-y-4">
      {loading && items.length === 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="border border-gray-200 shadow-sm rounded-xl animate-pulse">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gray-200 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="h-3 bg-gray-200 rounded w-16 mb-2" />
                  <div className="h-6 bg-gray-200 rounded w-12" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          {SUMMARY_CARDS.map(({ key, label, icon: Icon, accent, bg }) => (
            <Card key={key} className="border border-gray-200 shadow-sm rounded-xl">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${bg}`}>
                  <Icon className={`h-5 w-5 ${accent}`} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide truncate">{label}</div>
                  <div className={`text-xl font-bold mt-0.5 ${accent}`}>{summary[key]}</div>
                </div>
              </CardContent>
            </Card>
          ))}

          <Card className="border border-gray-200 shadow-sm rounded-xl">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-indigo-50">
                <ShoppingBag className="h-5 w-5 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide truncate">
                  Units Sold ({daysLabel})
                </div>
                <div className="text-xl font-bold mt-0.5 text-indigo-600">{unitsSold}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
          <CardTitle className="flex flex-col xl:flex-row xl:items-center gap-3 justify-between w-full">
            <div className="relative w-full xl:w-96">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by item, brand, category, size..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Period</span>
              <div className="flex rounded-lg border border-[#0263cc]/20 overflow-hidden">
                {DAYS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDaysFilter(opt.value)}
                    className={`px-3 py-1.5 text-xs font-semibold ${
                      daysFilter === opt.value ? "bg-[#0263cc] text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/dashboard/inventory/reports")}
                className="border-[#0263cc]/30 text-[#0263cc] hover:bg-[#0263cc]/5 rounded-lg flex items-center gap-1.5 px-2 sm:px-3 text-sm whitespace-nowrap"
              >
                <FileSpreadsheet className="h-4 w-4" />
                <span className="hidden sm:inline">Reports</span>
              </Button>
            </div>
          </CardTitle>

          <div className="flex flex-col gap-3 mt-4">
            <div className="flex flex-wrap rounded-lg border border-[#0263cc]/20 overflow-hidden w-fit">
              {TABS.map((tab) => {
                const Icon = tab.icon
                const count = tab.summaryKey ? summary[tab.summaryKey] : null
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${
                      activeTab === tab.id ? "bg-[#0263cc] text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {Icon && <Icon className="h-4 w-4" />}
                    {tab.label}
                    {count !== null && count > 0 && (
                      <span
                        className={`ml-0.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
                          activeTab === tab.id ? "bg-white/20" : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
            <MultiSelectFilterCombobox
              placeholder="Category"
              allLabel="All Categories"
              value={filterOptions.category}
              onValueChange={(value) => setFilterOptions({ ...filterOptions, category: value })}
              options={categories.map((category) => ({ key: category._id, value: category.name, label: category.name }))}
            />

            {isSubCategoryEnabled && (
              <MultiSelectFilterCombobox
                placeholder="Sub-category"
                allLabel="All Sub-categories"
                value={filterOptions.sub_category}
                onValueChange={(value) => setFilterOptions({ ...filterOptions, sub_category: value })}
                options={subCategoryOptions.map((subCategory) => ({ key: subCategory._id, value: subCategory.name, label: subCategory.name }))}
              />
            )}

            {brandOptions.length > 0 && (
              <MultiSelectFilterCombobox
                placeholder="Brand"
                allLabel="All Brands"
                value={filterOptions.brand}
                onValueChange={(value) => setFilterOptions({ ...filterOptions, brand: value })}
                options={brandOptions.map((brand) => ({ key: brand, value: brand, label: brand }))}
              />
            )}

            {sizes.length > 0 && (
              <MultiSelectFilterCombobox
                placeholder="Size"
                allLabel="All Sizes"
                value={filterOptions.size}
                onValueChange={(value) => setFilterOptions({ ...filterOptions, size: value })}
                options={sizes.map((size) => ({ key: size._id, value: size.name, label: size.name }))}
              />
            )}

            {isColorEnabled && colors.length > 0 && (
              <MultiSelectFilterCombobox
                placeholder="Color"
                allLabel="All Colors"
                value={filterOptions.color}
                onValueChange={(value) => setFilterOptions({ ...filterOptions, color: value })}
                options={colors.map((color) => ({ key: color._id, value: color.name, label: color.name }))}
              />
            )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {error && (
            <Alert variant="destructive" className="m-4 rounded-lg bg-red-50 text-red-800 border-red-200">
              <AlertDescription className="font-medium">{error}</AlertDescription>
            </Alert>
          )}

          {activeTab === "analysis" ? (
            <InventoryAnalyticsAnalysisTab analysis={analysis} loading={analysisLoading} error={analysisError} />
          ) : (
            <>
              <InventoryAnalyticsCharts charts={charts} loading={allItemsLoading} isSubCategoryEnabled={isSubCategoryEnabled} daysFilter={daysFilter} />
              <InventoryAnalyticsTable
                activeTab={activeTab}
                items={items}
                loading={loading}
                searchTerm={searchTerm}
                isColorEnabled={isColorEnabled}
                isSubCategoryEnabled={isSubCategoryEnabled}
                daysFilter={daysFilter}
                currentPage={currentPage}
                totalCount={totalCount}
                itemsPerPage={itemsPerPage}
                setCurrentPage={setCurrentPage}
                setItemsPerPage={setItemsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default InventoryAnalyticsDashboard
